import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Label } from '@antimatterhq/ui/components/label';
import { PresetHeading } from '../../_partials/PresetHeading';
import { SilverAddTables } from './partials/SilverAddTables';
import { SilverModifyTables } from './partials/SilverModifyTables';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import { getSilverInitialValues, setSilverFormValues } from '@/lib/datasource';
import {
  SilverDataSourceSchema,
  SilverDataSourceSchemaValues,
} from '@/zod/schemas/dataSource/Silver';
import { DataSourceNavigationButtons } from '@/components/button/dataSourceNavigation/DataSourceNavigationButtons';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { TwoColumnLayout } from '@/components/container';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import { useDebounceValidation } from '@/hooks/formik/useDebounceValidation';
import {
  FormAccordion,
  FormAccordionContent,
  FormAccordionTrigger,
  FormAccordionItem,
} from '@/components/form/accordion/FormAccordion';
import { FormSwitch } from '@/components/form/form';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

export type SilverFormProps = {
  submitAction: DatasourceSubmitType;
  setSubmitAction: (action: DatasourceSubmitType) => void;
  submitDisabled?: boolean;
};

const SilverForm = ({
  submitAction,
  setSubmitAction,
  submitDisabled = false,
}: SilverFormProps) => {
  const { handlePreviousStep, isSaving, setFormik } = useConfigDataSource();
  const { values, handleSubmit, dirty, handleChange } =
    useFormikContext<SilverDataSourceSchemaValues>();

  useEffect(() => {
    setFormik({ handleSubmit, dirty });
  }, [handleSubmit, dirty, setFormik]);

  return (
    <Form data-testid="silver-form">
      <DataSourceNavigationButtons
        disabled={isSaving || submitDisabled}
        goToPreviousStep={handlePreviousStep}
        handlePreviousStep={() => setSubmitAction('previous')}
        handleNextStep={() => setSubmitAction('next')}
        handleReview={() => setSubmitAction('review')}
        loadingNext={submitAction === 'next' && isSaving}
        loadingReview={submitAction === 'review' && isSaving}
        data-testid="silver-top-navigation-buttons"
      />

      <SilverModifyTables />

      <Spacer size="lg" />

      <SilverAddTables />

      <Spacer size="2xl" />

      {(!!values.addTables.length || !!values.modifyTables.length) && (
        <>
          <FormAccordion type="multiple">
            <FormAccordionItem value="advancedOptions">
              <FormAccordionTrigger data-testid="advanced-options-accordion">
                <Text size="lg">Advanced options</Text>
              </FormAccordionTrigger>
              <FormAccordionContent>
                <div
                  className="flex items-center gap-2"
                  data-testid="enable-switch-container"
                >
                  <FormSwitch
                    name="doNotMaterialize"
                    onChange={handleChange}
                    checked={values.doNotMaterialize}
                    data-testid="do-not-materialize-switch"
                  />
                  <Label htmlFor="doNotMaterialize" mb={0}>
                    Do not materialize silver tables
                  </Label>
                  <div>
                    <Text className="italic" size="sm" color="secondary">
                      (silver tables will still be computed to be used by gold
                      table definitions, but will not be saved)
                    </Text>
                  </div>
                </div>
              </FormAccordionContent>
            </FormAccordionItem>
          </FormAccordion>

          <Spacer size="2xl" />

          <DataSourceNavigationButtons
            disabled={isSaving || submitDisabled}
            goToPreviousStep={handlePreviousStep}
            handlePreviousStep={() => setSubmitAction('previous')}
            handleNextStep={() => setSubmitAction('next')}
            handleReview={() => setSubmitAction('review')}
            loadingNext={submitAction === 'next' && isSaving}
            loadingReview={submitAction === 'review' && isSaving}
            data-testid="silver-bottom-navigation-buttons"
          />
        </>
      )}
    </Form>
  );
};

function DataSourceSilverContent() {
  const { dataSource, handleNextStep, handleReview, save, handlePreviousStep } =
    useConfigDataSource();
  const [submitAction, setSubmitAction] = useState<DatasourceSubmitType>('');
  const formik = useFormikWithAsyncValidation({
    initialValues: getSilverInitialValues(dataSource),
    validate: toFormikValidate(SilverDataSourceSchema),
    validationSchema: toFormikValidationSchema(SilverDataSourceSchema),
    validateOnChange: false,
    onSubmit: async (newValues) => {
      // update state
      const newDataSource = setSilverFormValues(dataSource, newValues);
      const result = await save(newDataSource);

      if (!result) {
        return;
      }

      // go to next step
      if (submitAction === 'next') {
        handleNextStep();
      } else if (submitAction === 'review') {
        handleReview();
      } else if (submitAction === 'previous') {
        handlePreviousStep();
      }
    },
  });

  useDebounceValidation(formik);

  return (
    <TableOfContentsProvider>
      <TwoColumnLayout.Root data-testid="silver-layout-root">
        <TwoColumnLayout.LeftColumn>
          <PresetHeading />
          <FormikProvider value={formik}>
            <SilverForm
              submitAction={submitAction}
              setSubmitAction={setSubmitAction}
              submitDisabled={formik.submitDisabled}
            />
          </FormikProvider>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <TableOfContents data-testid="silver-table-of-contents" />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </TableOfContentsProvider>
  );
}

export default function DataSourceSilver() {
  return (
    <AsyncValidationProvider>
      <DataSourceSilverContent />
    </AsyncValidationProvider>
  );
}
